import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Header from "./components/Header";
import Materials from "./components/Materials";
import History from "./components/History";
import Chat from "./components/Chat";
import ChatHistory from "./components/ChatHistory";
import RFQ from "./components/RFQ";
import RFQArchive from "./components/RFQArchive";
import Groups from "./components/Groups";
import AdminPanel from "./components/AdminPanel";
import Login from "./components/Login";
import Loading from "./components/Loading";
import { getApiUrl } from "./config/api";
import { useSettings } from "./context/SettingsContext";
import { loginWithAuth0, validateAdminStatus } from "./services/authService";

export default function App() {
  const { isAuthenticated, isLoading, user, logout } = useAuth0();
  const { settings } = useSettings();
  const [chatHeight, setChatHeight] = useState(200);
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(224);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(456);
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(false);
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [dealersLoading, setDealersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("rfq"); // "rfq", "chat", or "archive"
  const [backendUser, setBackendUser] = useState(null);
  const [backendLoading, setBackendLoading] = useState(true);
  const [accountDisabled, setAccountDisabled] = useState(false);
  const [accountNotEnabled, setAccountNotEnabled] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeRfq, setActiveRfq] = useState(null); // Track the currently selected RFQ for chat
  const [outstandingRfqs, setOutstandingRfqs] = useState([]);
  
  // Use backend user ID as clientId (will be set after backend login)
  const clientId = backendUser?.id || 0;
  const clientEmail = backendUser?.email || backendUser?.auth0Email || user?.email || "no-email@example.com";
  const clientName = backendUser?.name || user?.name || "Client";

  // Apply dark mode class to document
  useEffect(() => {
    if (settings?.generalSettings?.enableDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [settings?.generalSettings?.enableDarkMode]);

  // Login to backend after Auth0 authentication
  useEffect(() => {
    const loginToBackend = async () => {
      if (!isAuthenticated || !user) {
        setBackendLoading(false);
        return;
      }

      try {
        const userData = await loginWithAuth0(user);
        
        // Check if user has client role
        if (!userData.role || userData.role.toLowerCase() !== 'client') {
          console.warn('[App] User does not have client role:', userData.role);
          setAccountNotEnabled(true);
          setBackendLoading(false);
          // Auto logout after showing message
          setTimeout(() => {
            logout({ logoutParams: { returnTo: window.location.origin } });
          }, 5000);
          return;
        }
        
        // Check if user is enabled
        if (!userData.isEnabled) {
          console.warn('[App] User account is not enabled');
          setAccountNotEnabled(true);
          setBackendLoading(false);
          return;
        }
        
        setBackendUser(userData);
        
        // Check admin status
        const adminStatus = await validateAdminStatus(user.sub);
        setIsAdmin(adminStatus);
        
        setAccountDisabled(false);
        setAccountNotEnabled(false);
        setNetworkError(false);
      } catch (error) {
        console.error('[App] Backend login error:', error);
        
        if (error.message === 'NETWORK_ERROR') {
          console.error('[App] Network error, cannot reach backend');
          setNetworkError(true);
          // Don't auto logout for network errors - give user a chance to retry
        } else if (error.message === 'ACCOUNT_DISABLED') {
          setAccountDisabled(true);
          setAccountNotEnabled(true);
          // Auto logout after showing disabled message
          setTimeout(() => {
            logout({ logoutParams: { returnTo: window.location.origin } });
          }, 5000);
        } else if (error.message === 'USER_NOT_FOUND') {
          console.warn('[App] User not found in backend, needs manual creation');
          setAccountNotEnabled(true);
          // Auto logout after showing message
          setTimeout(() => {
            logout({ logoutParams: { returnTo: window.location.origin } });
          }, 5000);
        } else {
          // Any other login failure should also block access
          console.error('[App] Login endpoint failed, blocking access');
          setAccountNotEnabled(true);
          // Auto logout after showing message
          setTimeout(() => {
            logout({ logoutParams: { returnTo: window.location.origin } });
          }, 5000);
        }
      } finally {
        setBackendLoading(false);
      }
    };

    loginToBackend();
  }, [isAuthenticated, user, logout]);

  // Set userId and userEmail in localStorage for settings API
  useEffect(() => {
    if (isAuthenticated && user && backendUser) {
      localStorage.setItem("userId", String(clientId));
      localStorage.setItem("userEmail", clientEmail);
      localStorage.setItem("isAdmin", String(isAdmin));
    }
  }, [clientId, clientEmail, isAuthenticated, user, backendUser, isAdmin]);

  // Helper to open chat for a given RFQ - behavior depends on current tab
  const openChatForRfq = useCallback((rfq) => {
    if (!rfq) return;
    setActiveRfq(rfq);
    // Don't automatically switch tabs - let the Open Chat button handle that
  }, []);

  // Handler for "Open Chat" button in modal - decides what to do based on current tab
  const handleOpenChatClick = useCallback((rfq) => {
    if (!rfq) return;
    setActiveRfq(rfq);
    
    if (activeTab === "chat") {
      // Already on chat tab - ChatHistory will auto-select this conversation
    } else {
      // On RFQ or Archive tab - the bottom Chat panel should open/select this chat
      setActiveRfq({ ...rfq, _openInBottomPanel: true });
    }
  }, [activeTab]);

  // Listen for global open-chat events (emitted by RFQStream notifications)
  useEffect(() => {
    const onOpenChatEvent = (e) => {
      try {
        const d = e.detail || {};
        // Normalize payload to what handleOpenChatClick expects
        const payload = {
          rfqNumber: d.rfqNumber,
          dealer: d.dealerName,
          dealerId: d.dealerId,
          material: d.material,
        };
        handleOpenChatClick(payload);
      } catch (err) {
        console.error('[App] open-chat event handler error:', err);
      }
    };

    window.addEventListener('open-chat', onOpenChatEvent);
    return () => window.removeEventListener('open-chat', onOpenChatEvent);
  }, [handleOpenChatClick]);

  // Handler for when history items are updated (real-time via SSE)
  const handleHistoryUpdate = useCallback((historyItems) => {
    // Convert history items to outstandingRfqs format
    const rfqs = historyItems
      .filter(item => item.rfqNumber)
      .map(item => ({
        rfqNumber: item.rfqNumber,
        rfqId: item.id || null,
        dealerName: item.dealerCompany || item.dealerName || item.dealer || 'Company',
        dealer: item.dealerCompany || item.dealerName || item.dealer || 'Company',
        dealerId: item.dealerId || null,
        material: item.material || null,
        subMaterial: item.subMaterial || null,
        state: item.state,
      }));
    
    setOutstandingRfqs(rfqs);
  }, []); // Empty deps - function doesn't depend on any props/state

  // Fetch outstanding RFQs for populating chat dealer list
  useEffect(() => {
    let mounted = true;
    const loadOutstanding = async () => {
      if (!clientId) return;
      
      try {
        const res = await fetch(getApiUrl(`/api/history/client/${encodeURIComponent(clientId)}`));
        if (!res.ok) throw new Error('Failed to fetch history');
        const data = await res.json();
        
        const items = Array.isArray(data) ? data : data?.history || data?.items || [];
        
        // Include ALL RFQs for chat (not just outstanding)
        // Users should be able to chat about any RFQ, regardless of state
        // The rfqNumber might be at top level OR nested in rfq object
        const outstanding = items
          .filter(i => i.rfqNumber || (i.rfq && i.rfq.rfqNumber))
          .map(i => {
            const rfqNumber = i.rfqNumber || (i.rfq && i.rfq.rfqNumber);
            const dealerName = i.dealerCompany || i.dealerName || i.dealer || (i.rfq && i.rfq.dealerName) || 'Company';
            
            return {
              rfqNumber: rfqNumber,
              rfqId: i.rfq?.id || i.rfqId || null,
              dealerName: dealerName,
              dealer: dealerName,
              dealerId: i.dealerId || (i.dealer && i.dealer.id) || null,
              material: i.material || (i.rfq && i.rfq.material) || null,
              subMaterial: i.subMaterial || (i.rfq && i.rfq.subtype) || null,
              state: i.state,
            };
          });
        
        if (mounted) setOutstandingRfqs(outstanding);
      } catch (err) {
        console.error('[App] Failed to load outstanding RFQs:', err);
      }
    };
    loadOutstanding();
    return () => { mounted = false; };
  }, [clientId]);

  // Apply dark mode
  useEffect(() => {
    if (settings.generalSettings.enableDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [settings.generalSettings.enableDarkMode]);

  useEffect(() => {
    // Fetch dealers from API
    const fetchDealers = async () => {
      setDealersLoading(true);
      try {
        const response = await fetch(getApiUrl('/api/suppliers'));
        if (!response.ok) {
          throw new Error('Failed to fetch dealers');
        }
        const data = await response.json();
        setDealers(data);
        console.log('Fetched dealers:', data);
      } catch (error) {
        console.error('Error fetching dealers:', error);
        // Fallback to mock data if API fails
        setDealers({});
      } finally {
        setDealersLoading(false);
      }
    };

    fetchDealers();
  }, []);

  // Flatten dealers to a single list for Chat
  const allDealers = useMemo(() => {
    const map = new Map();
    const add = (d) => {
      if (!d) return;
      const id = d.id || `${d.name || "Unknown"}-${d.location || ""}`;
      if (!map.has(id)) {
        map.set(id, {
          id,
          name: d.name || d.id || "Unknown Dealer",
          location: d.location || d.state || "",
        });
      }
    };

    if (Array.isArray(dealers)) {
      dealers.forEach(add);
    } else if (dealers && typeof dealers === "object") {
      // Handles nested fallback structure
      Object.values(dealers).forEach((arr) => {
        if (Array.isArray(arr)) {
          arr.forEach((catObj) => {
            if (catObj && typeof catObj === "object") {
              Object.values(catObj).forEach((list) => {
                if (Array.isArray(list)) list.forEach(add);
              });
            }
          });
        }
      });
    }
    return Array.from(map.values());
  }, [dealers]);

  const handleVerticalDrag = (e) => {
    e.preventDefault();
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
    const handleMouseMove = (e) => {
      const windowHeight = window.innerHeight;
      const newHeight = windowHeight - e.clientY;
      const minHeight = 100;
      const maxHeight = windowHeight / 2;
      setChatHeight(Math.min(Math.max(newHeight, minHeight), maxHeight));
    };
    const handleMouseUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleLeftSidebarDrag = (e) => {
    e.preventDefault();
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
    const handleMouseMove = (e) => {
      const newWidth = e.clientX;
      const minWidth = 150;
      const maxWidth = 400;
      setLeftSidebarWidth(Math.min(Math.max(newWidth, minWidth), maxWidth));
    };
    const handleMouseUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleRightSidebarDrag = (e) => {
    e.preventDefault();
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
    const handleMouseMove = (e) => {
      const windowWidth = window.innerWidth;
      const newWidth = windowWidth - e.clientX;
      const minWidth = 150;
      const maxWidth = windowWidth * 0.6;
      setRightSidebarWidth(Math.min(Math.max(newWidth, minWidth), maxWidth));
    };
    const handleMouseUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Show loading screen while Auth0 is initializing
  if (isLoading) {
    return <Loading />;
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  // Show loading while backend authentication is in progress
  if (backendLoading) {
    return <Loading />;
  }

  // Show network error screen (before other checks so user can retry)
  if (networkError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 rounded shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 text-yellow-600 dark:text-yellow-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Connection Error</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Unable to connect to the server. Please check your internet connection and try again.
          </p>
          {user?.email && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded p-3 mb-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">Logged in as:</span> {user.email}
              </p>
            </div>
          )}
          <div className="space-y-2">
            <button
              onClick={() => {
                setNetworkError(false);
                setBackendLoading(true);
                window.location.reload();
              }}
              className="w-full bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition-colors"
            >
              Retry Connection
            </button>
            <button
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              className="w-full bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-100 font-medium py-2 px-4 rounded transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show account not enabled screen (before account disabled check for better UX)
  if (accountNotEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 text-red-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            Your account is not authorized to access this platform. Only users with the "client" role can log in. Please contact an administrator if you believe this is an error.
          </p>
          {user?.email && (
            <div className="bg-gray-50 rounded p-3 mb-4">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Email:</span> {user.email}
              </p>
            </div>
          )}
          <p className="text-sm text-gray-500 mb-4">
            You will be logged out in 5 seconds...
          </p>
          <button
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            className="w-full bg-gray-800 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded transition-colors"
          >
            Logout Now
          </button>
        </div>
      </div>
    );
  }

  // Show account disabled message
  if (accountDisabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 text-red-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Disabled</h2>
          <p className="text-gray-600 mb-4">
            Your account has been disabled by an administrator. Please contact support for assistance.
          </p>
          {user?.email && (
            <div className="bg-gray-50 rounded p-3 mb-4">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Email:</span> {user.email}
              </p>
            </div>
          )}
          <p className="text-sm text-gray-500">
            You will be logged out in 5 seconds...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100 dark:bg-gray-900">
      <Header isAdmin={isAdmin} clientId={clientId} openChatForRfq={handleOpenChatClick} />
      <div className="flex flex-1 overflow-hidden">
        <Materials 
          width={leftSidebarWidth} 
          onDragStart={handleLeftSidebarDrag}
          selectedMaterial={selectedMaterial}
          onSelectMaterial={(material) => {
            setSelectedMaterial(material);
            // Always switch to RFQ tab when selecting a material
            setActiveTab("rfq");
          }}
        />
        <main className="flex-1 bg-gray-200 dark:bg-gray-800 flex flex-col">
          {/* Tab Navigation */}
          <div className="bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 px-4 flex gap-1">
            <button
              onClick={() => setActiveTab("rfq")}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "rfq"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >
              RFQ
            </button>
            <button
              onClick={() => setActiveTab("archive")}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "archive"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >
              Archive
            </button>
            <button
              onClick={() => setActiveTab("groups")}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "groups"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >
              Groups
            </button>
            <button
              onClick={() => setActiveTab("chat")}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "chat"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >
              Chat
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab("admin")}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "admin"
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                }`}
              >
                Admin Panel
              </button>
            )}
          </div>

          {/* Tab Content */}
          {activeTab === "rfq" && (
            <RFQ selectedMaterial={selectedMaterial} dealers={dealers} dealersLoading={dealersLoading} clientId={clientId} />
          )}
          
          {activeTab === "archive" && (
            <RFQArchive clientId={clientId} onOpenChat={handleOpenChatClick} />
          )}

          {activeTab === "groups" && (
            <Groups 
              onOpenChat={handleOpenChatClick} 
              clientId={clientId}
              onOpenRFQ={(material) => {
                setActiveTab("rfq");
              }}
            />
          )}

          {activeTab === "chat" && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <ChatHistory clientId={clientId} clientName={clientName} activeRfq={activeRfq} />
            </div>
          )}
          
          {activeTab === "admin" && isAdmin && (
            <AdminPanel />
          )}
          
          {/* Chat panel - always rendered to maintain WebSocket connections and tabs */}
          {activeTab !== "admin" && (
            <Chat 
              height={chatHeight}
              isCollapsed={isChatCollapsed}
              onToggle={() => setIsChatCollapsed(!isChatCollapsed)}
              onDragStart={handleVerticalDrag}
              allDealers={allDealers}
              clientId={clientId}
              clientName={clientName}
              activeRfq={activeRfq}
              outstandingRfqs={outstandingRfqs}
              onChatOpened={activeRfq?._openInBottomPanel}
              activeTab={activeTab}
            />
          )}
        </main>
        <History 
          width={rightSidebarWidth}
          isCollapsed={isRightSidebarCollapsed}
          onToggle={() => setIsRightSidebarCollapsed(!isRightSidebarCollapsed)}
          onDragStart={handleRightSidebarDrag}
          clientId={clientId}
          onRfqSelect={openChatForRfq}
          onOpenChat={handleOpenChatClick}
          onHistoryUpdate={handleHistoryUpdate}
        />
      </div>
    </div>
  );
}
